import { useStyleConfigV2 } from '@tectonic/elemason-components';
import { isBlank } from '@tectonic/utils';
import clsx from 'clsx';
import { isString, noop } from 'lodash-es';
import { useCallback, type FC, type FormEvent } from 'react';
import { ElemasonJsonSchemaForm } from '../../JSONSchemaForm';
import { useUserProfile } from './hooks';

import type { CustomValidator } from '@rjsf/utils';
import type { UserProfileFormWidgetProps } from './UserProfileForm.types';

const customValidate: CustomValidator = (formData, errors, uiSchema) => {
  if (!formData) {
    return errors;
  }
  for (const key of Object.keys(formData)) {
    // ui:rules is our custom property to add custom validation rules
    const isRequired = !!uiSchema?.[key]?.['ui:rules']?.required;
    // by default rjsf validate empty value but it doesn't validate blank strings.
    if (isRequired && isString(formData[key])) {
      if (isBlank(formData[key])) {
        errors[key]?.addError(`${key} is required`);
      }
    }
  }
  return errors;
};

const UserProfileFormWidget: FC<UserProfileFormWidgetProps> = ({ widget }) => {
  const { config } = widget;
  const [style, className] = useStyleConfigV2(config?.container);
  const { formData, schema, uiSchema, onChange, formRef, isReady } =
    useUserProfile(widget);

  const onSubmit = useCallback((_data: any, event: FormEvent<any>) => {
    event.preventDefault();
  }, []);

  return (
    <div style={style} className={clsx(className)}>
      {isReady ? (
        <ElemasonJsonSchemaForm
          ref={formRef}
          onChange={onChange}
          schema={schema}
          onSubmit={onSubmit}
          uiSchema={uiSchema}
          formData={formData}
          className="w-full"
          showErrorList={false}
          onError={noop}
          // TODO: Use more generic solutions using ui:schema as mentioned here
          // https://rjsf-team.github.io/react-jsonschema-form/docs/usage/validation/#custom-validation-rules
          customValidate={customValidate}
        />
      ) : null}
    </div>
  );
};

UserProfileFormWidget.displayName = 'UserProfileFormWidget';

export { UserProfileFormWidget };
